import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../APIs/API";
import { getEmployeeToken, clearEmployeeToken } from "../utils/helper";

const labelStyle = { fontWeight: 600, color: "#555", fontSize: 13, marginBottom: 2 };
const valueStyle = { fontSize: 14, color: "#222", marginBottom: 0 };

function ProfileField({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value || "—"}</div>
    </div>
  );
}

const EmployeePortal = () => {
  const navigate = useNavigate();
  const token = getEmployeeToken();

  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingAtt, setLoadingAtt] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    (async () => {
      const result = await new API().getEmployeeMe(token);
      setLoadingProfile(false);
      if (result?.data) {
        setProfile(result.data);
      } else {
        toast.error("Session expired. Please log in again.");
        clearEmployeeToken();
        navigate("/employee-login");
      }
    })();
  }, []);

  const fetchAttendance = async () => {
    if (!startDate) { toast.warning("Please select a start date."); return; }
    setLoadingAtt(true);
    const result = await new API().getEmployeeMyAttendance(token, startDate, endDate);
    setLoadingAtt(false);
    if (result?.data) {
      setAttendance(result.data);
      if (result.data.length === 0) toast.info("No attendance records found for the selected period.");
    } else {
      toast.error("Failed to load attendance records.");
    }
  };

  useEffect(() => {
    if (profile) fetchAttendance();
  }, [profile]);

  const handleLogout = () => {
    clearEmployeeToken();
    navigate("/employee-login");
  };

  if (loadingProfile) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f6f9" }}>
      {/* Top Bar */}
      <div
        style={{
          backgroundColor: "#1a237e",
          color: "#fff",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        <div>
          <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: 0.5 }}>PNG Time Access</span>
          <span
            style={{
              marginLeft: 14,
              fontSize: 13,
              backgroundColor: "rgba(255,255,255,0.15)",
              padding: "2px 10px",
              borderRadius: 12,
            }}
          >
            Employee Self-Service Portal
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {profile && (
            <span style={{ fontSize: 14, opacity: 0.9 }}>
              {profile.name_report}
            </span>
          )}
          <button
            className="btn btn-sm btn-outline-light"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "28px auto", padding: "0 16px" }}>
        {/* Profile Card */}
        {profile && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: "22px 28px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
              marginBottom: 24,
            }}
          >
            <h5 style={{ fontWeight: 700, marginBottom: 20, color: "#1a237e", borderBottom: "2px solid #e8eaf6", paddingBottom: 10 }}>
              My Profile
            </h5>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0 32px" }}>
              <ProfileField label="Full Name" value={profile.name_report} />
              <ProfileField label="Employee No." value={profile.employee_no} />
              <ProfileField label="Email" value={profile.email} />
              <ProfileField label="Location" value={profile.admin_desc} />
              <ProfileField label="Position" value={profile.occup_pos_title || "Unattached"} />
              <ProfileField label="Position No." value={profile.position_no || (profile.position_attached === false ? "Unattached" : "—")} />
              <ProfileField label="Classification" value={profile.class_desc} />
              <ProfileField label="Award" value={profile.award_desc} />
              <ProfileField label="Employment Status" value={profile.emp_status} />
              <ProfileField label="Gender" value={profile.gender} />
              <ProfileField label="Date of Birth" value={profile.date_of_birth} />
              <ProfileField label="Age" value={profile.age != null ? `${profile.age} yrs` : "—"} />
              <ProfileField label="Account No." value={profile.account_no} />
              <ProfileField label="Paypoint" value={profile.paypoint_desc} />
              <ProfileField label="First Commencement" value={profile.first_commence} />
            </div>
          </div>
        )}

        {/* Attendance Card */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 10,
            padding: "22px 28px",
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
          }}
        >
          <h5 style={{ fontWeight: 700, marginBottom: 20, color: "#1a237e", borderBottom: "2px solid #e8eaf6", paddingBottom: 10 }}>
            My Attendance Records
          </h5>

          {/* Date Range Filter */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <label style={{ ...labelStyle, display: "block" }}>From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 160 }}
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label style={{ ...labelStyle, display: "block" }}>To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 160 }}
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={fetchAttendance}
              disabled={loadingAtt}
              style={{ paddingLeft: 20, paddingRight: 20 }}
            >
              {loadingAtt ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Loading…
                </>
              ) : (
                "Search"
              )}
            </button>
          </div>

          {/* Attendance Table */}
          {attendance.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-bordered table-hover table-sm" style={{ fontSize: 14 }}>
                <thead style={{ backgroundColor: "#e8eaf6" }}>
                  <tr>
                    <th style={{ width: 40, textAlign: "center" }}>#</th>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Worked Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ textAlign: "center", color: "#888" }}>{idx + 1}</td>
                      <td>{row.date || "—"}</td>
                      <td>{row.clocked_in || "—"}</td>
                      <td>{row.clocked_out || "—"}</td>
                      <td>{row.worked_hours || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, color: "#888", textAlign: "right", marginTop: 4 }}>
                {attendance.length} record{attendance.length !== 1 ? "s" : ""} found
              </div>
            </div>
          ) : (
            !loadingAtt && (
              <div style={{ textAlign: "center", color: "#aaa", padding: "32px 0", fontSize: 14 }}>
                No attendance records to display. Select a date range and click Search.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeePortal;
